import { useState } from "react"
import { type Appointment } from "@/types"
import { Calendar, Clock, Edit, CheckCircle, AlertCircle, XCircle } from "lucide-react"
import EditAppointmentModal from "@/components/Appointment/EditAppointmentModal"
import { format } from "date-fns"
import { Badge } from "@/components/ui/badge"

type Props = {
  appointments: Appointment[]
  onRefresh?: () => void
}

const getStatusIcon = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />;
    case 'cancelled':
      return <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
    case 'scheduled':
    default:
      return <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
    case 'cancelled':
      return 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800';
    case 'scheduled':
    default:
      return 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
  }
};

export default function AppointmentList({ appointments, onRefresh }: Props) {
  const [editing, setEditing] = useState<Appointment | null>(null)

  return (
    <div className="space-y-3">
      {appointments.map((appt) => (
        <div
          key={appt.id}
          className="bg-card border border-border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              {/* Date and Time */}
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">
                  {format(new Date(appt.date), "PPP")}
                </span>
                {appt.time && (
                  <div className="flex items-center gap-1 ml-2">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{appt.time}</span>
                  </div>
                )}
              </div>
              
              {/* Reason */}
              <p className="text-sm text-foreground font-medium mb-2">{appt.reason}</p>
              
              {/* Status */}
              <div className="flex items-center gap-2">
                {getStatusIcon(appt.status)}
                <Badge 
                  variant="outline" 
                  className={`text-xs ${getStatusColor(appt.status)}`}
                >
                  {(appt.status || 'scheduled').charAt(0).toUpperCase() + (appt.status || 'scheduled').slice(1)}
                </Badge>
              </div>
            </div>
            
            {/* Edit Button */}
            <button
              onClick={() => setEditing(appt)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
            >
              <Edit className="w-3 h-3" />
              Edit
            </button>
          </div>
        </div>
      ))}

      {editing && (
        <EditAppointmentModal
          isOpen={true}
          onClose={() => setEditing(null)}
          appointment={editing}
          onUpdated={() => {
            setEditing(null);
            if (onRefresh) {
              onRefresh();
            }
          }}
        />
      )}
    </div>
  )
}